/**
 * Shared MIME type constants for AI file attachments.
 *
 * Must stay in sync with allowedAttachmentContentTypes in
 * internal/core/graph_api/resolvers/ai_helpers.go
 */

/** MIME types accepted by the file-input `accept` attribute (no octet-stream — that's a browser fallback, not user-selectable). */
export const ATTACHMENT_ACCEPT =
  'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/csv,text/html,application/json';

/** Max characters to show in an attachment pill before truncating with ellipsis. */
export const MAX_DISPLAY_LENGTH = 64;

/** Truncate a file name for display in attachment pills, preserving the extension. */
export const truncateFileName = (name: string) => {
  if (name.length <= MAX_DISPLAY_LENGTH) {
    return name;
  }
  const extIdx = name.lastIndexOf('.');
  if (extIdx === -1) {
    return `${name.slice(0, MAX_DISPLAY_LENGTH)}\u2026`;
  }
  const ext = name.slice(extIdx);
  const stem = name.slice(0, MAX_DISPLAY_LENGTH - ext.length - 1);
  return `${stem}\u2026${ext}`;
};

/** Full set of MIME types the backend allows, including application/octet-stream for unknown browser types. */
export const ALLOWED_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/html',
  'application/json',
  'application/octet-stream',
]);

/** Maximum number of files that can be attached to a single AI prompt. */
export const MAX_ATTACHMENTS = 5;

/** Maximum size for text-based attachments — must match webPageMaxSizeBytes in web fetch tool. */
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

/** Maximum size for binary attachments — must match webBinaryContentMaxSizeBytes in web fetch tool. */
export const MAX_BINARY_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

/** MIME types the backend treats as binary content with the stricter 5MB limit. */
export const BINARY_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
]);

/** Whether a file's MIME type is classified as binary (image/pdf) with the stricter size limit. */
export const isBinaryFile = (file: File) => BINARY_MIME_TYPES.has(file.type);

/** Whether a file's MIME type is in the backend allowlist. Empty type (browser fallback) is allowed. */
export const isAllowedFileType = (file: File) => {
  // Empty file.type (browser fallback) maps to application/octet-stream which is allowed
  if (!file.type) {
    return true;
  }
  return ALLOWED_MIME_TYPES.has(file.type);
};
